import { FC, useEffect, useMemo } from 'react';
import { Field } from 'redux-form';

import PinInputField from 'inquiry/components/PinInputField';
import { parseEmptyStringToNull } from 'inquiry/lib/form';
import { useApply, useValidators } from 'inquiry/lib/jsonLogic';
import type { InputConfirmationCodeComponent } from 'lib/interfaces/ui-components';
import type { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import type { Inquiry } from 'verify/models/inquiry';

interface InputConfirmationCodeProps {
  actionsApi: UiComponentActionsApi;
  component: InputConfirmationCodeComponent;
  inquiry: Inquiry;
}

const InputConfirmationCode: FC<InputConfirmationCodeProps> = ({
  actionsApi,
  component,
  inquiry,
}: InputConfirmationCodeProps) => {
  const { disabled, hidden, optional, validations } = component.attributes;
  const formField = component.name;

  const apply = useApply(formField, inquiry.attributes['next-step'].name);

  const validators = useValidators({ hidden, optional, validations });

  // TODO: figure out how to test this
  const isHidden = apply<boolean>(hidden);

  useEffect(() => {
    if (isHidden) {
      // Setting to '' removes the field value from form state entirely, unlike null which sets value to null.
      actionsApi.setField(formField, '');
    }
  }, [isHidden]);

  const validatorsWithLengthValidation = useMemo(() => {
    const lengthValidator = (val: string) => {
      // Don't validate empty values -- assume that the optional rule will handle that.
      if (isHidden || !val || val?.length >= 4) {
        return null;
      } else {
        return optional?.message;
      }
    };
    return [...validators, lengthValidator];
  }, [validators, optional?.message, isHidden]);

  if (isHidden) {
    return null;
  }

  return (
    <Field
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      component={PinInputField}
      disabled={disabled}
      id={formField}
      name={formField}
      parse={parseEmptyStringToNull}
      validate={validatorsWithLengthValidation}
      styles={component.styles}
    />
  );
};

export default InputConfirmationCode;
